<template>
  <div class="product-peried">
    <page-title :label="$t('product_dashboard_main.title_emissions_per_product')" iconPath="page-title-icon.svg" />
    <emission-filter
      v-if="isLoading"
      v-model="dataFilter"
      :disabled="checkLoading"
      :branchIds="branchIds"
      :isHasBranchNull="isHasBranchNull"
      :productList="productList"
      :categoryList="categoryList"
      @onChangeSelectedData="handleChangeSelectedData"
      @onChangeFilterType="handleDataEmissionFilterChanged"
      @onChangeFilterOptions="handleChangeBranchIds"
    />
    <tab-header :tab="type" @update:tab="handleUpdateTab" />
    <div v-if="true" class="product-peried-chart mt-12">
      <v-tabs-items v-model="type" class="product-peried-tab">
        <v-tab-item class="product-peried-tab-item">
          <template v-if="isChartYear">
            <emission-chart
              period="Y"
              :filter-type="dataFilter.filterType"
              :dataChart="dataChart"
              @selectChart="handleSelectChart"
            />
            <chart-detail isChartYear :chartDetail="chartDetail" class="month-chart-detail" />
          </template>
        </v-tab-item>
        <v-tab-item class="product-peried-tab-item">
          <template v-if="!isChartYear">
            <emission-chart
              period="M"
              :filter-type="dataFilter.filterType"
              :dataChart="dataChart"
              @selectChart="handleSelectChart"
            />
            <chart-detail :chartDetail="chartDetail" class="month-chart-detail" />
          </template>
        </v-tab-item>
      </v-tabs-items>
    </div>
    <chart-detail-popup
      v-if="dialogChartDetail"
      :detailDataChart="chartDetail"
      :dialog="dialogChartDetail"
      :isChartYear="isChartYear"
      @close="dialogChartDetail = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import { chartTitleMonthYear } from '@/utils/getTextWithCondition';
import { getPeriodProductDashboardGeneral } from '@/api/product/emissionsProduct';

import PageTitle from '@/components/products/common/page-title.vue';
import TabHeader from '@/components/products/common/tab-header.vue';
import EmissionFilter from '@/components/products/common/emission-filter.vue';
import ChartDetail from '@/components/products/charts/period/chart-detail.vue';
import EmissionChart from '@/components/products/charts/period/column-chart/emission-chart.vue';
import ChartDetailPopup from '@/components/products/dialogs/DetailChartPopupSP.vue';

export default {
  name: 'ProductByPeriodNewDashboard',
  components: {
    PageTitle,
    TabHeader,
    ChartDetail,
    EmissionFilter,
    EmissionChart,
    ChartDetailPopup,
  },
  props: {
    branchIds: {
      type: Array,
      default: () => [],
    },
    products: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    isHasBranchNull: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    productList: {
      type: Array,
      default: () => [],
    },
    categoryList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      chartDetail: {},
      dataChart: [],
      dialogChartDetail: false,
      type: 0,
      dataFilter: {
        filterType: 1,
        selectedData: [],
      },
      shouldUpdateLoading: false,
      isLoading: false,
    };
  },
  computed: {
    ...mapState('product', ['newDashboard', 'colors']),
    getListProductName() {
      const realProductList = this.products.filter((item) => item.id);
      const mapProductListData = (list) =>
        list.map((item, index) => ({
          id: item.id,
          name: item?.name,
          color: this.colors[index],
          lotNumber: item?.lot_number,
        }));

      if (this.dataFilter.selectedData?.length > 0) {
        // has selected products

        // filter products selected matching pulldown current
        const realProductListSelected = realProductList.filter((item) =>
          this.dataFilter.selectedData?.includes(item.id),
        );

        if (realProductListSelected.length > 0) {
          return mapProductListData(realProductListSelected);
        }
      }
      return mapProductListData(realProductList); // don't selected products
    },
    checkLoading() {
      return this.shouldUpdateLoading ? this.loading : false;
    },
    dashboardStoreData() {
      return this.newDashboard?.emissionsByProducts;
    },
    isChartYear() {
      return this.type === 0;
    },
  },
  methods: {
    ...mapActions('product', ['updateNewEmissionsByProducts']),
    handleUpdateTab(newTab) {
      this.type = newTab;
      this.getChartData();
    },
    handleSelectChart(chartItem, isClicked = false) {
      if (this.isMobile && isClicked) {
        this.dialogChartDetail = true;
      }
      // update duration text
      let duration = '';
      let chartItems = {};
      // column chart
      if (!this.isChartYear) {
        const month = moment(chartItem.month, 'M').format('M');
        const year = moment(chartItem.year, 'YYYY').format('YYYY');
        duration = chartTitleMonthYear(3, year, 0, month); // tab = 0 | 1 | 2 | 3,  year, order = 0, month, type = ''
      } else {
        const year = chartItem?.year;
        duration = chartTitleMonthYear(0, year); // tab = 0 | 1 | 2 | 3, year, order = 0, month, type = ''
      }

      chartItems = {
        ...chartItem,
        period: this.type,
        time_range: duration,
        month: chartItem?.month,
        total_emission: chartItem?.total_emission,
        up: chartItem?.up,
        ratio: chartItem?.ratio,
      };

      this.chartDetail = chartItems;

      this.updateDataToStore();
    },
    async handleDataEmissionFilterChanged(value) {
      const listType = this.getDataStoreTypeList(value);
      this.shouldUpdateLoading = true;
      this.dataFilter.selectedData = [...(this.dashboardStoreData?.[listType] || [])];
      this.updateNewEmissionsByProducts({
        ...this.dashboardStoreData,
        filterType: value,
      });
      await this.getChartData();
      this.shouldUpdateLoading = false;
    },
    updateDataToStore() {
      this.updateNewEmissionsByProducts({
        filterType: this.dataFilter.filterType || 1,
        period: this.type,
        selectedData: {
          yearTab: this.isChartYear ? this.chartDetail.year : this.dashboardStoreData?.selectedData?.yearTab,
          monthTab: {
            month: !this.isChartYear ? this.chartDetail.month : this.dashboardStoreData?.selectedData?.monthTab.month,
            year: !this.isChartYear ? this.chartDetail.year : this.dashboardStoreData?.selectedData?.monthTab.year,
          },
          indexSelected: this.dashboardStoreData?.selectedData?.indexSelected,
          pageCurrent: this.dashboardStoreData?.selectedData?.pageCurrent,
        },
        productFilterList: this.dashboardStoreData?.productFilterList || [],
        categoryFilterList: this.dashboardStoreData?.categoryFilterList || [],
      });
    },
    async getChartData() {
      const payload = {
        contractor_id: this.$store.state.userData.contractor,
        type: this.type || 0, // (0: year) or (1: month)
        branch_id: this.branchIds,
        branch_null: this.isHasBranchNull ? 1 : null,
      };

      if (this.dataFilter.filterType === 1) {
        payload.cfp_product_ids = this.dataFilter.selectedData || [];
        payload.chart_type = 0;
      }
      if (this.dataFilter.filterType === 2) {
        payload.cfp_product_categories = this.dataFilter.selectedData || [];
        payload.chart_type = 1;
      }

      try {
        if (this.shouldUpdateLoading) {
          this.loading = true;
        }
        const { data } = await getPeriodProductDashboardGeneral(payload, this.$route);


        this.dataChart = data.map((item) => {
          const formattedMonth = moment(
            `${item.year}-${String(item.month).padStart(2, '0')}`,
            'YYYY-MM'
          ).format('M');

          return {
            ...item,
            time_range: this.type === 0 ? item.year : `${item.year}/${formattedMonth}`,
          };
        }) || [];
      } finally {
        if (this.shouldUpdateLoading) {
          this.loading = false;
        }
      }
    },
    async handleChangeSelectedData(value) {
      const updateDataList = this.getDataStoreTypeList();
      this.updateNewEmissionsByProducts({
        ...this.dashboardStoreData,
        [updateDataList]: [...(value || [])],
      });
      this.shouldUpdateLoading = true;
      await this.getChartData();
      this.shouldUpdateLoading = false;
    },
    getDataStoreTypeList(value = null) {
      return (value || this.dataFilter.filterType) === 1 ? 'productFilterList' : 'categoryFilterList';
    },
    handleChangeBranchIds(dataList) {
      // remove selected data if not exist in dataList list
      const newDataListArr = [...dataList.map((item) => item.id)];
      this.dataFilter.selectedData = this.dataFilter.selectedData.filter((item) => newDataListArr.includes(item)); // get selected data in list (ignore another data)
      // update store data
      this.handleChangeSelectedData(this.dataFilter.selectedData);
    },
  },
  watch: {
    isMobile: {
      handler(value) {
        if (!value) this.dialogChartDetail = false;
      },
    },
    branchIds: {
      async handler() {
        if (this.dataFilter.filterType === 2) {
          await this.getChartData();
        }
      },
    },
  },
  mounted() {
    this.type = this.dashboardStoreData?.period || 0;
    const listType = this.getDataStoreTypeList(this.dashboardStoreData?.filterType || 1);
    this.dataFilter = {
      filterType: this.dashboardStoreData?.filterType || 1,
      selectedData: this.dashboardStoreData?.[listType] || [],
    };
    this.isLoading = true;
  },
};
</script>

<style lang="scss" scoped>
.product-peried {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  overflow: hidden;
  &-chart {
    width: 100%;
    .product-peried-tab {
      background-color: transparent;
      width: 100%;
      overflow: unset;
    }
    .month-chart-detail {
      display: none;
    }
  }
}

@include desktop {
  .product-peried {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    // gap: 16px;
    width: 100%;
    &-chart {
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 0px;
      gap: 16px;
      .month-chart {
        width: 65%;
      }
      .month-chart-detail {
        display: flex;
        width: calc(100% - 16px - 54px - 65%);
      }
    }
  }
}
@media (max-width: 450px) {
  .product-peried {
    .product-peried-chart {
      margin-bottom: 0 !important;
    }
    .product-peried {
      margin-bottom: 16px !important;
    }
  }
}
</style>
